import React, { useState, useEffect } from 'react';

const FeaturesPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 8 AI Modules with DIFFERENT copy than homepage
  const aiModules = [
    {
      icon: "🔬",
      title: "Campaign Detective",
      subtitle: "AI Campaign Forensics",
      description: "Forensic-level performance analysis reveals exactly where your campaigns are hemorrhaging money. AI examines spend efficiency, audience targeting quality, and conversion paths with surgical precision.",
      metrics: ["Real-time diagnostics", "Severity classification", "Waste quantification"],
      benefits: [
        "Instant severity ratings: Identify Champions worth scaling and Critical campaigns draining budget",
        "Pinpoint budget waste sources: Poor targeting, audience fatigue, platform inefficiencies",
        "Track spend allocation patterns across all active campaigns in your account",
        "Detect audience overlap causing campaigns to compete against each other",
        "Calculate exact waste amounts with confidence scores up to 99.2%"
      ],
      color: "from-red-500 to-orange-500"
    },
    {
      icon: "🎯",
      title: "Waste Hunter",
      subtitle: "Cross-Campaign Waste Detection",
      description: "Advanced pattern recognition across your entire campaign portfolio identifies hidden inefficiencies. Discovers budget leaks, redundant targeting, and optimization blind spots you'd never catch manually.",
      metrics: ["Portfolio analysis", "Pattern detection", "Proactive alerting"],
      benefits: [
        "Cross-campaign overlap detection: Find campaigns targeting identical audiences",
        "Budget efficiency scoring across your entire advertising portfolio",
        "Identify micro-waste that compounds into significant monthly losses",
        "Receive predictive alerts when waste patterns begin emerging",
        "Get ranked optimization opportunities by potential impact"
      ],
      color: "from-orange-500 to-red-600"
    },
    {
      icon: "📊",
      title: "Deep Campaign Analysis",
      subtitle: "Strategic Intelligence Engine",
      description: "Context-aware campaign evaluation that understands your marketing objectives. Provides strategic recommendations calibrated to whether you're driving leads, traffic, awareness, or sales.",
      metrics: ["Objective intelligence", "Competitive context", "Strategic planning"],
      benefits: [
        "Objective-specific metrics: Lead campaigns analyzed for CPL, traffic for CTR/CPC, sales for ROAS",
        "Industry benchmark comparisons using real-time market data",
        "Understand audience behavior patterns and response psychology",
        "Identify competitive gaps and market positioning opportunities",
        "Generate quarter-long optimization roadmaps with projected ROI"
      ],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: "💬",
      title: "Aura AI Assistant",
      subtitle: "Conversational Marketing Intelligence",
      description: "Natural language interface to your marketing data. Ask complex questions in plain English and receive strategic answers backed by your campaign performance and market intelligence.",
      metrics: ["Natural queries", "Contextual answers", "Strategic counsel"],
      benefits: [
        "Query campaigns using everyday language instead of complex filters",
        "Receive instant analysis without navigating dashboards or reports",
        "Get strategic recommendations, not just raw data dumps",
        "Access market intelligence and competitive insights through conversation",
        "Context-aware responses that remember your business goals and constraints"
      ],
      color: "from-pink-500 to-purple-600"
    },
    {
      icon: "🌐",
      title: "Platform Intelligence",
      subtitle: "Global Platform Optimizer",
      description: "Data-driven platform selection and budget allocation guidance across global markets. Analyzes performance potential across advertising channels in 195+ countries with 180+ currency support.",
      metrics: ["Global coverage", "Multi-currency", "Channel optimization"],
      benefits: [
        "Platform mix recommendations tailored to your target markets",
        "Budget distribution strategies across advertising channels",
        "Currency-normalized performance analysis across 180+ currencies",
        "Market-specific platform effectiveness data for 195+ countries",
        "Optimal timing strategies for platform expansion and scaling"
      ],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "🌍",
      title: "Global Market Intelligence",
      subtitle: "Strategic Market Analysis",
      description: "Comprehensive market landscape analysis across industries and geographies. Delivers competitive intelligence, regulatory insights, and opportunity identification for international expansion.",
      metrics: ["Market research", "Competitive intel", "Entry strategies"],
      benefits: [
        "Industry-specific performance benchmarks across 50+ sectors",
        "Competitive landscape mapping and gap analysis",
        "Market entry playbooks with timing and budget recommendations",
        "Regulatory compliance requirements and advertising restrictions",
        "Opportunity scoring based on market conditions and competition"
      ],
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: "✍️",
      title: "Content Generation Engine",
      subtitle: "Psychological Copywriting AI",
      description: "Framework-based copywriting system trained on proven conversion principles. Generates messaging optimized with psychological triggers from established marketing methodologies.",
      metrics: ["Evidence-based", "Framework-driven", "Conversion-focused"],
      benefits: [
        "Apply principles from Cialdini, Schwartz, Sugarman, and other frameworks",
        "Generate copy variations across 30+ marketing content formats",
        "Embed psychological triggers: scarcity, social proof, authority, reciprocity",
        "Analyze existing content and identify psychological weaknesses",
        "Create high-converting messaging backed by behavioral science"
      ],
      color: "from-amber-500 to-yellow-500"
    },
    {
      icon: "📈",
      title: "Decision Tracking System",
      subtitle: "Marketing Accountability Engine",
      description: "Structured decision documentation and outcome measurement system. Creates institutional knowledge by tracking what was changed, why, predicted results, and actual performance impact.",
      metrics: ["Decision logging", "Outcome tracking", "Knowledge building"],
      benefits: [
        "Document every campaign modification with rationale and predictions",
        "Compare predicted outcomes against actual performance results",
        "Receive AI-generated accountability scores from 1-10 based on accuracy",
        "Access strategic insights with real-time market benchmark data",
        "Build searchable decision database showing what works and what fails",
        "Track performance across 8 objective categories: leads, traffic, sales, etc."
      ],
      color: "from-indigo-500 to-purple-500"
    }
  ];

  const stats = [
    { number: "$2M+", label: "Waste Detected", icon: "💰" },
    { number: "195", label: "Countries", icon: "🌍" },
    { number: "180+", label: "Currencies", icon: "💱" },
    { number: "99.2%", label: "AI Accuracy", icon: "🤖" }
  ];

  const integrations = [
    { 
      name: "Facebook Ads", 
      icon: "📘", 
      status: "Active", 
      features: ["Full campaign sync", "Performance tracking", "Waste detection"]
    },
    { 
      name: "Google Ads", 
      icon: "🔍", 
      status: "Coming Soon", 
      features: ["Search campaigns", "Display network", "Shopping ads"]
    },
    { 
      name: "LinkedIn Ads", 
      icon: "💼", 
      status: "Coming Soon", 
      features: ["B2B campaigns", "Lead gen forms", "InMail ads"]
    },
    { 
      name: "TikTok Ads", 
      icon: "🎵", 
      status: "Coming Soon", 
      features: ["Video campaigns", "Spark ads", "Creator marketplace"]
    }
  ];

  const technicalFeatures = [
    {
      title: 'Real-Time Market Intelligence',
      description: 'Live competitive data and market trends powered by Google Search grounding for current insights',
      icon: '🔍'
    },
    {
      title: 'Multi-Currency Intelligence',
      description: 'Localized cost analysis and performance measurement across 180+ global currencies',
      icon: '💱'
    },
    {
      title: 'Psychological Framework Library',
      description: 'Proven copywriting methodologies from Cialdini, Schwartz, Sugarman, and Halbert',
      icon: '🧠'
    },
    {
      title: 'Decision Accountability Engine',
      description: 'Track decisions, measure outcomes vs predictions, build institutional marketing knowledge',
      icon: '📊'
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Navigation - EXACT SAME */}
      <nav className={`fixed top-0 left-0 right-0 z-50 py-2 transition-all duration-300 ${
        scrollY > 50 ? 'bg-black/95 backdrop-blur-xl border-b border-orange-400/20' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="flex items-center justify-between h-14 gap-4">
            <a href="/" className="flex items-center cursor-pointer hover:scale-105 transition-transform">
              <span className="text-xl sm:text-2xl font-black text-orange-400 tracking-tight">WhyDidItDie</span>
            </a>

            <div className="hidden lg:flex items-center flex-1 justify-center gap-5 xl:gap-8">
              <a href="/#modules" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">8 AI Modules</a>
              <a href="/features" className="text-orange-400 font-medium text-sm">Features</a>
              <a href="/pricing" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Pricing</a>
              <a href="/how-it-works" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">How It Works</a>
              <a href="/integrations" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Integrations</a>
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <a href="https://app.whydiditdie.com/login" className="px-4 py-2 border border-white/20 rounded-lg font-semibold text-sm hover:bg-white/10 hover:border-white/40 transition-all hover:-translate-y-0.5">
                Login
              </a>
              <a href="https://app.whydiditdie.com/signup" className="px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg font-bold text-sm shadow-lg shadow-orange-500/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/60 transition-all">
                Start Free Trial
              </a>
            </div>

            <button 
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 border border-white/30 text-white hover:bg-white/20 transition-all"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden fixed top-[68px] left-0 right-0 bottom-0 bg-black/97 backdrop-blur-xl p-6 overflow-y-auto">
            <div className="space-y-1">
              <a href="/#modules" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>8 AI Modules</a>
              <a href="/features" className="block py-4 text-lg font-semibold border-b border-white/10 text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="/pricing" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Pricing</a>
              <a href="/how-it-works" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>How It Works</a>
              <a href="/integrations" className="block py-4 text-lg font-semibold hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Integrations</a>
            </div>
            <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-white/15">
              <a href="https://app.whydiditdie.com/login" className="py-4 border-2 border-white/40 rounded-xl font-semibold text-center hover:bg-white/10 transition-all" onClick={() => setIsMenuOpen(false)}>
                Login
              </a>
              <a href="https://app.whydiditdie.com/signup" className="py-4 bg-gradient-to-r from-orange-400 to-red-500 rounded-xl font-bold text-center shadow-lg shadow-orange-500/40 hover:-translate-y-0.5 hover:shadow-xl transition-all" onClick={() => setIsMenuOpen(false)}>
                Start Free Trial
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - SAME LAYOUT, DIFFERENT COPY */}
      <section className="pt-32 pb-20 px-4 text-center bg-gradient-to-b from-orange-400/10 via-transparent to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-400/20 border border-orange-400/30 rounded-full px-4 py-2 text-sm font-semibold text-orange-300 mb-6">
            <span>🤖</span>
            Advanced Marketing Intelligence Platform
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            Detailed Feature Breakdown
          </h1>
          
          <p className="text-xl text-white/80 mb-8 leading-relaxed max-w-3xl mx-auto">
            Explore the complete capabilities of each AI module. See exactly how our intelligence engines work together to optimize your marketing performance.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-12">
            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-black text-orange-400">{stat.number}</span>
                  <span className="text-xs text-white/60 font-medium">{stat.label}</span>
                </div>
                {index < stats.length - 1 && <div className="w-px h-9 bg-white/20 hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>

          <a href="https://app.whydiditdie.com/signup" className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 px-8 py-4 rounded-2xl text-lg font-bold shadow-xl shadow-orange-500/40 hover:-translate-y-1 hover:shadow-2xl transition-all">
            <span>🚀</span>
            Start Free 2-Days Trial
            <span>→</span>
          </a>
        </div>
      </section>

      {/* 8 AI Modules Section - SAME GRID, DIFFERENT COPY */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Complete Module Reference Guide
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
              Technical specifications and capability breakdowns for all eight AI-powered marketing intelligence modules
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiModules.map((module, index) => (
              <div
                key={module.title}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/30 hover:border-orange-400/30 transition-all cursor-pointer"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${module.color} flex items-center justify-center text-2xl flex-shrink-0`}>
                    {module.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{module.title}</h3>
                    <p className="text-sm text-white/60">{module.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-sm leading-relaxed text-white/80 mb-4">
                  {module.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {module.metrics.map((metric, i) => (
                    <span key={i} className="bg-orange-400/20 border border-orange-400/30 rounded-2xl px-3 py-1 text-xs font-semibold text-orange-300">
                      {metric}
                    </span>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-xs font-bold text-orange-400 mb-2 uppercase">Core Capabilities:</h4>
                  <ul className="space-y-1">
                    {module.benefits.slice(0, 3).map((benefit, i) => (
                      <li key={i} className="flex items-start text-xs text-white/70">
                        <span className="text-green-400 mr-2">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Features - SAME LAYOUT */}
      <section className="py-20 px-4 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Technical Infrastructure
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              Enterprise-grade technology powering intelligent marketing analysis and optimization
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {technicalFeatures.map((feature, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-orange-400/30 transition-all">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{feature.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm leading-relaxed text-white/70">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Integrations - SAME LAYOUT */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Advertising Platform Connectivity
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              Direct integration status with major advertising platforms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-orange-400/30 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{integration.icon}</div>
                    <div className="font-bold text-white">{integration.name}</div>
                  </div>
                  <div
                    className={`text-xs px-2 py-1 rounded-lg font-semibold ${
                      integration.status === 'Active'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}
                  >
                    {integration.status}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {integration.features.map((feature, i) => (
                    <div key={i} className="flex items-center text-sm text-white/70">
                      <span className="text-orange-400 mr-2">•</span>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - SAME LAYOUT */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[-50px] left-[-50px] w-[200px] h-[200px] bg-white/10 rounded-full animate-pulse" />
          <div className="absolute bottom-[-100px] right-[-80px] w-[300px] h-[300px] bg-white/10 rounded-full animate-pulse" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Experience All Eight Modules
          </h2>
          <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-2xl mx-auto">
            Access the complete marketing intelligence platform with your free 2-days trial
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="https://app.whydiditdie.com/signup" className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-2xl">
              🚀 Start Free Trial
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>Full module access</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>30-day evaluation period</span>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 inline-flex items-center gap-8">
            <div className="text-center">
              <div className="text-2xl font-black">10,000+</div>
              <div className="text-xs uppercase tracking-wide opacity-80">Active Users</div>
            </div>
            <div className="w-px h-10 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-black">$2M+</div>
              <div className="text-xs uppercase tracking-wide opacity-80">Waste Detected</div>
            </div>
            <div className="w-px h-10 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-black">99.2%</div>
              <div className="text-xs uppercase tracking-wide opacity-80">AI Accuracy</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl font-black text-orange-400">WhyDidItDie</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                AI-powered marketing intelligence platform
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <div className="space-y-2 text-sm text-white/60">
                <a href="/#modules" className="block hover:text-orange-400">Features</a>
                <a href="/pricing" className="block hover:text-orange-400">Pricing</a>
                <a href="/how-it-works" className="block hover:text-orange-400">How It Works</a>
                <a href="/integrations" className="block hover:text-orange-400">Integrations</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <div className="space-y-2 text-sm text-white/60">
                <a href="/about" className="block hover:text-orange-400">About</a>
                <a href="/blog" className="block hover:text-orange-400">Blog</a>
                <a href="/contact" className="block hover:text-orange-400">Contact</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Follow Us</h4>
              <div className="grid grid-cols-2 gap-2">
                <a href="https://www.instagram.com/ai_whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>📸</span> Instagram
                </a>
                <a href="https://x.com/whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>𝕏</span> Twitter
                </a>
                <a href="https://www.facebook.com/whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>👍</span> Facebook
                </a>
                <a href="https://www.tiktok.com/@app.whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>🎵</span> TikTok
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 text-center text-sm text-white/60">
            © {new Date().getFullYear()} WhyDidItDie. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;